import { benefitStates, recommendedFare } from './fare_ladder.js';
import type { FareTextClassifier, FlightFareSource, FlightTariffSource } from './fare_source.js';

/**
 * **A aritmética da seleção de tarifas — pura, sem React.**
 *
 * Nasceu dentro do `FareExpansion` e saiu de lá quando a tela "Escolha as
 * tarifas de cada voo" (#2262) virou a segunda superfície a fazer as MESMAS
 * contas: qual é o preço da combinação corrente, se ela fecha, e qual perna
 * ficou sem despacho. São regras em que errar custa dinheiro (o combo bundled
 * precifica o PAR, e somar as duas pernas dobra o valor) — duas cópias
 * divergiriam na primeira mudança de contrato do provider.
 *
 * O que NÃO mora aqui é o desenho: a expansão inline e a página de tarifas
 * compartilham a MESMA árvore de cards (`FareRail`/`FareFamilyCard`); este
 * módulo só guarda a aritmética da seleção.
 */

/** Seleção corrente (ids opacos fareKey/comboId). */
export interface FareSel {
  outbound?: string;
  return?: string;
  bundled?: string;
}

/** As duas pernas da seleção corrente, na ordem `[ida, volta]`. Tupla e não
 *  array: quem consome depende de haver exatamente duas. */
export type SelectedLegs<F extends FlightFareSource = FlightFareSource> = [
  F | undefined,
  F | undefined,
];

export interface SelectionSummary<F extends FlightFareSource = FlightFareSource> {
  total: number;
  currency: string;
  names: string;
  complete: boolean;
  legs: SelectedLegs<F>;
}

/**
 * Combos bundled como tarifas — preço do combo = o TOTAL DO PAR, que
 * o flight-business já carrega EM CADA perna do combo (`c.departing.price` ===
 * `c.returning.price`). Somar as duas pernas DOBRA o preço.
 *
 * ATENÇÃO: o spread é de `c.departing`, então os `benefits` — e portanto as
 * linhas do card — descrevem SÓ a perna de ida. O aviso de bagagem logo abaixo
 * lê o par de verdade (`view.bundled[].departing`/`.returning`), então os dois
 * podem discordar na tela: o combo Flex do fixture mostra `sem reembolso` (ida)
 * embora a volta reembolse. Consertar exigiria mesclar as duas pernas por
 * dimensão (o pior dos dois lados), o que muda o contrato do card — fica
 * registrado, não é bug de renderização.
 */
export function comboFares<F extends FlightFareSource>(view: FlightTariffSource<F>): F[] {
  return view.bundled.map((c) => ({
    ...c.departing,
    fareId: c.fareId,
    price: {
      amount: c.departing.price.amount,
      currency: c.departing.price.currency,
    },
  }));
}

/**
 * **Existe escada de volta?** Fonte única da pergunta, e quem responde é o
 * TARIFF: `returning` só é preenchido quando o modal-session precificou um PAR
 * (`buildFlightTariffView` decide round-trip por `offer.returningKey`, e o ramo
 * one-way emite `returning: []` por construção). Quem decide o layout, o preço e
 * o default é esta função.
 *
 * Perguntar TAMBÉM à oferta (`offer.returnLeg`) parecia cinto extra e era um
 * portão errado: a oferta da fase 2 do ida-e-volta chega do
 * `POST /flight/return-search`, que projeta a volta do par como UMA perna (sem
 * `returnLeg`) com o preço do par. Nesse par — o normal, quando as pernas não
 * vêm da mesma fonte tarifária e o `flightSearchTariff` devolve `bundled: []` —
 * a escada da volta ficava escondida, o rodapé cobrava a ida sozinha (metade do
 * par) e o pick saía com um `departing_N` solto: um id de LINHA da matriz, que o
 * `splitModalFarePair` (`flight/shared/booking`) recusa e o modal-session não
 * conhece como `pricingOptionId`. O caso que o termo extra dizia proteger (oferta
 * de ida só recebendo tariff com `returning`) não tem produtor, e onde ele agisse
 * o pick de uma perna já seria irresgatável do mesmo jeito.
 */
export function hasReturnLadder(view: FlightTariffSource): boolean {
  return view.returning.length > 0;
}

const cents = (fare: FlightFareSource): number => Math.round(fare.price.amount * 100);

/**
 * A tarifa MAIS BARATA de uma lista — a que deu o preço do card. Preço 0 não é
 * preço: é o fallback do tariff-view quando o fornecedor não mandou valor, e
 * nunca pode vencer como "a mais barata". Lista só com preço 0 cai na régua da
 * recomendada (abrir a escada sem seleção travaria o CTA). Empate de preço
 * (ao centavo) também cai na régua da recomendada: entre duas de mesmo valor,
 * fica a que despacha.
 */
function cheapestFare<F extends FlightFareSource>(
  fares: readonly F[],
  classify: FareTextClassifier,
): F | undefined {
  const priced = fares.filter((fare) => cents(fare) > 0);
  if (priced.length === 0) return recommendedFare(fares, classify);
  const floor = Math.min(...priced.map(cents));
  return recommendedFare(
    priced.filter((fare) => cents(fare) === floor),
    classify,
  );
}

/**
 * Defaults de seleção pro tariff carregado: a combinação MAIS BARATA — a
 * mais barata por perna, ou o combo mais barato. É ela que deu o preço em
 * destaque do card (o preço da busca é o piso da escada), então abrir as
 * tarifas sem tocar em nada mostra no rodapé o MESMO total do card (#5491).
 * A recomendada (a mais barata com despacho, `recommendedFare`) segue como
 * SELO no `FareRail`; ela só não é mais o default.
 */
export function defaultSel(view: FlightTariffSource, classify: FareTextClassifier): FareSel {
  if (view.bundled.length > 0) {
    return { bundled: cheapestFare(comboFares(view), classify)?.fareId };
  }
  return {
    outbound: cheapestFare(view.departing, classify)?.fareId,
    ...(hasReturnLadder(view) ? { return: cheapestFare(view.returning, classify)?.fareId } : {}),
  };
}

function summarizeBundledSelection<F extends FlightFareSource>(
  view: FlightTariffSource<F>,
  combos: readonly F[],
  fareId: string | undefined,
): SelectionSummary<F> {
  const pickedCombo = combos.find((fare) => fare.fareId === fareId);
  const pickedBundle = view.bundled.find((fare) => fare.fareId === fareId);
  return {
    total: pickedCombo?.price.amount ?? 0,
    currency: pickedCombo?.price.currency ?? 'BRL',
    names: pickedCombo?.name ?? '',
    complete: Boolean(pickedCombo),
    legs: [pickedBundle?.departing, pickedBundle?.returning],
  };
}

function summarizeSplitSelection<F extends FlightFareSource>(
  view: FlightTariffSource<F>,
  sel: FareSel,
): SelectionSummary<F> {
  const pickedOut = view.departing.find((fare) => fare.fareId === sel.outbound);
  const pickedRet = view.returning.find((fare) => fare.fareId === sel.return);
  return {
    total: (pickedOut?.price.amount ?? 0) + (pickedRet?.price.amount ?? 0),
    currency: pickedOut?.price.currency ?? 'BRL',
    names: [pickedOut?.name, pickedRet?.name].filter(Boolean).join(' + '),
    complete: Boolean(pickedOut && (!hasReturnLadder(view) || pickedRet)),
    legs: [pickedOut, pickedRet],
  };
}

/**
 * O que a seleção corrente vale e se ela fecha. O ramo bundled/split mora SÓ
 * aqui: antes ele se repetia cinco vezes no corpo do `FareExpansion`, com
 * `sel.bundled` resolvido por dois caminhos diferentes (o combo
 * achatado em tarifa para o preço, e o par cru de `view.bundled` para as pernas).
 */
export function summarizeSelection<F extends FlightFareSource>(
  view: FlightTariffSource<F>,
  combos: readonly F[],
  sel: FareSel,
): SelectionSummary<F> {
  return view.bundled.length > 0
    ? summarizeBundledSelection(view, combos, sel.bundled)
    : summarizeSplitSelection(view, sel);
}

export interface AsymmetricChecked {
  /** A perna que fica ATRÁS: sem despacho, ou despachando mediante taxa. */
  leg: 'outbound' | 'return';
  kind: 'absent' | 'fee';
}

/**
 * A perna que ficou sem despacho (ou só com despacho pago) quando a outra
 * despacha de graça. Bagagem assimétrica é desastre operacional (descobrir no
 * balcão) e nenhuma referência avisa, porque nenhuma delas deixa as pernas
 * independentes — é o custo do nosso diferencial, e ele precisa de aviso.
 *
 * Três variantes, nenhuma inventada: incluído × ausente → aviso; incluído ×
 * taxa → aviso brando; `unknown` em qualquer perna → silêncio, porque avisar
 * exige saber.
 */
export function asymmetricChecked(
  legs: SelectedLegs,
  classify: FareTextClassifier,
): AsymmetricChecked | null {
  const states = legs.map((f) => (f ? benefitStates(f, classify).checked : undefined));
  if (states.includes(undefined) || states.includes('unknown')) return null;
  const included = states.indexOf('included');
  if (included < 0) return null;
  const other = included === 0 ? 1 : 0;
  const otherState = states[other];
  if (otherState === 'included') return null;
  return {
    leg: other === 0 ? 'outbound' : 'return',
    kind: otherState === 'fee' ? 'fee' : 'absent',
  };
}
